import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

interface SalesGoal {
  id: string;
  restaurant_id: string;
  month: number;
  year: number;
  goal_amount: number;
  created_at: string;
  updated_at: string;
}

export function useSalesGoals() {
  const { activeRestaurant } = useRestaurantContext();
  const [goals, setGoals] = useState<SalesGoal[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchGoals = useCallback(async (year?: number) => {
    if (!activeRestaurant) return;
    
    setIsLoading(true);
    try {
      let query = supabase
        .from("sales_goals" as any)
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("year", { ascending: false })
        .order("month", { ascending: false });

      if (year) {
        query = query.eq("year", year);
      }

      const { data, error } = await query as any;

      if (error) throw error;
      setGoals((data as SalesGoal[]) || []);
    } catch (error) {
      console.error("Error fetching goals:", error);
      toast.error("Erro ao carregar metas");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const setGoal = useCallback(async (month: number, year: number, amount: number) => {
    if (!activeRestaurant) return false;

    try {
      const { error } = await supabase
        .from("sales_goals" as any)
        .upsert({
          restaurant_id: activeRestaurant.id,
          month,
          year,
          goal_amount: amount,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "restaurant_id,month,year",
        });

      if (error) throw error;
      
      toast.success("Meta salva com sucesso!");
      await fetchGoals(year);
      return true;
    } catch (error) {
      console.error("Error setting goal:", error);
      toast.error("Erro ao salvar meta");
      return false;
    }
  }, [activeRestaurant, fetchGoals]);

  const getGoal = useCallback((month: number, year: number): SalesGoal | undefined => {
    return goals.find(g => g.month === month && g.year === year);
  }, [goals]);

  return {
    goals,
    isLoading,
    fetchGoals,
    setGoal,
    getGoal,
  };
}
